import { useCallback, useMemo } from 'react';
import { usePatientVirtualAppointments } from '../../hooks/useVirtualAppointments';
import type { VirtualAppointmentDto } from '../../services/virtualAppointments';
import { useVirtualAppointmentSummaries } from './useVirtualAppointmentSummaries';
import type { VirtualAppointmentRecapListProps } from '@odo/shared-ui/clinical/virtualAppointments/VirtualAppointmentRecapList';
import type { VirtualAppointmentLoadStatus } from '@odo/shared-ui/clinical/virtualAppointments/types';

const NO_VAS: VirtualAppointmentDto[] = [];

// Recap props for one document group's saved VAs, with loading/error/retry wired in.
export function useDocumentVirtualAppointmentRecap(
    patientId: string,
    groupId: string | undefined
): VirtualAppointmentRecapListProps {
    const { data, isPending, isError, isFetching, refetch } = usePatientVirtualAppointments(
        patientId,
        { groupId },
        { enabled: groupId !== undefined }
    );
    const summaries = useVirtualAppointmentSummaries(data ?? NO_VAS);
    const onRetry = useCallback(() => void refetch(), [refetch]);
    const failed = isError && !isFetching;
    const status: VirtualAppointmentLoadStatus = failed
        ? 'error'
        : isPending || isError
          ? 'loading'
          : 'ready';
    return useMemo(() => ({ summaries, status, onRetry }), [summaries, status, onRetry]);
}
